/**
 * TA capacity planning — **what the team can close**.
 *
 * The one question the tab exists to answer, so it is the one thing above the fold.
 *
 * ⚠️ IT IS TEAM-FIRST SINCE Sep 29 2026 (Theresa: *"I want to understand visually how many of the
 * planned roles / target set the team can realistically close"*). What changed is not the
 * arithmetic — it is which number leads and how much room the clusters take.
 *
 *   before   a sentence about the plan, then three full-width cluster bars taking most of the card
 *   now      one bar for the TEAM against the commitment, the plan as a second line, and the
 *            clusters as a compact strip underneath with the detail one click away
 *
 * ⚠️ THE HEADLINE MEASURES AGAINST THE COMMITTED TARGET where TA has set one, and against the plan
 * otherwise (Theresa's call, Sep 29 2026). The two are deliberately different numbers — demand is
 * what the business asked for, the target is what TA undertook — and a team-first view is about the
 * undertaking. Both are stated, so neither can be mistaken for the other, and the fallback is
 * labelled rather than silent: "we can close 38 of 40" and "38 of 40, because nobody has committed
 * a target yet" are not the same claim.
 *
 * ⚠️ WHAT DID NOT CHANGE, and must not: the closable figure is `covered`, summed PER CLUSTER and
 * never pooled. A surplus in Core Functions does not fill an Operations vacancy, because the
 * recruiter who has time is not the recruiter who knows the pipeline. Demoting the clusters
 * visually is a statement about attention, not about the maths — which is exactly why the stranded
 * hours are named in the line under the headline instead of disappearing with the bars.
 */
import { useState } from 'react';
import type { CapacityResult, ClusterCoverage } from '../../../utils/taCapacityModel';
import { ProgressBar, Section, WhatThisCounts, clamp, pctRaw } from '../tracking/trackingUi';

export function Coverage({
  result,
  horizonLabel,
  committedTarget,
}: {
  result: CapacityResult;
  horizonLabel: string;
  /** What TA undertook for this period, where a target has been committed. */
  committedTarget: number | null;
}) {
  const [openClusters, setOpenClusters] = useState(false);

  // The commitment leads when there is one; the plan is the honest fallback, and says so.
  const against = committedTarget != null && committedTarget > 0 ? committedTarget : result.demand;
  const measuringTarget = committedTarget != null && committedTarget > 0;
  const closable = Math.min(result.covered, against);
  const share = against > 0 ? closable / against : 0;
  const short = against - closable > 0.5;

  return (
    <Section
      title="What the team can close"
      aside={`${horizonLabel} · ${result.months.toFixed(1)} months of runway`}
    >
      {against <= 0 ? (
        <p data-testid="coverage-headline" className="mt-3 text-lg text-gray-900">
          Nothing to close — there is no demand and no committed target for this period.
        </p>
      ) : (
        <>
          <p
            data-testid="coverage-headline"
            className="mt-3 text-2xl leading-tight text-gray-900"
          >
            <b className="font-semibold tabular-nums">{Math.round(closable)}</b>
            <span className="text-gray-500"> of </span>
            <b className="font-semibold tabular-nums">{Math.round(against)}</b>{' '}
            <span className="text-gray-500">
              {measuringTarget ? 'we committed to' : 'open roles in the plan'}
            </span>{' '}
            <span className={`font-semibold ${short ? 'text-amber-700' : 'text-emerald-700'}`}>
              · {pctRaw(share)}
            </span>
          </p>

          {/*
            ⚠️ ONE bar, and it is the TEAM's. Three cluster bars were the first thing the eye
            landed on, which put the split above the answer — and the split is the second question,
            asked only once the first one has been read.
          */}
          <ProgressBar pct={clamp(share)} tone={short ? 'warn' : 'ok'} />

          <p data-testid="coverage-basis" className="mt-2.5 text-sm text-gray-600">
            {Math.round(result.capacity)} hires of capacity over {result.months.toFixed(1)} months
            {result.strandedCapacity > 0.5 && result.shortfall > 0.5 && (
              <span className="text-amber-700">
                {' '}
                · {Math.round(result.strandedCapacity)} of it stranded in clusters that do not need
                it and cannot lend it
              </span>
            )}
            .
          </p>

          {/*
            The OTHER denominator, always stated. Two numbers described as one is the failure this
            tab is most exposed to, so whichever one is not leading is named on its own line rather
            than left for someone to infer from the section they last scrolled past.
          */}
          <p data-testid="coverage-other" className="mt-1 text-sm text-gray-500">
            {measuringTarget ? (
              <>
                {Math.round(result.demand)} open roles in the plan —{' '}
                <b className="font-medium tabular-nums text-gray-700">
                  {Math.round(result.covered)}
                </b>{' '}
                of them coverable.
              </>
            ) : (
              <>No committed target for this period yet, so this measures against the plan.</>
            )}
            <WhatThisCounts
              label="What the team can close"
              why="Capacity is FTE × hires per month × the months of runway, summed per cluster and never pooled — a surplus in one cluster cannot fill a vacancy in another, so the closable figure is the sum of what each cluster can do for itself. It is measured against the committed target where TA has set one, because a team-first view is about what we undertook; the hiring plan's own demand is stated beside it."
            />
          </p>
        </>
      )}

      {/*
        ⚠️ SECONDARY, not gone. The company figure hides the thing a lead acts on — 14 spare in one
        cluster and 14 short in another is a company gap of zero and fourteen hires nobody can make
        — so the one-line-per-cluster strip is always visible and carries the verdict word. The
        bars, the FTE and the intern split are behind the toggle.
      */}
      <div className="mt-5 border-t border-gray-100 pt-3">
        <button
          type="button"
          onClick={() => setOpenClusters((v) => !v)}
          aria-expanded={openClusters}
          className="flex w-full items-baseline justify-between gap-3 text-left"
        >
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            By cluster
          </span>
          <span className="text-xs font-medium text-blue-600">
            {openClusters ? 'Hide the detail' : 'Show the detail'}
          </span>
        </button>

        <div className="mt-2 space-y-1.5">
          {result.clusters.map((c) =>
            openClusters ? <ClusterRow key={c.key} row={c} /> : <ClusterLine key={c.key} row={c} />
          )}
        </div>

        {openClusters && (
          <p className="mt-3 text-[11px] text-gray-400">
            Bar is capacity, line is demand. Summed per cluster — capacity does not pool.
          </p>
        )}
      </div>
    </Section>
  );
}

/** The collapsed form: the verdict in words, no chart. */
function ClusterLine({ row }: { row: ClusterCoverage }) {
  const short = row.gap < -0.5;
  return (
    <div
      data-testid={`coverage-line-${row.key}`}
      className="flex items-baseline justify-between gap-3 text-sm"
    >
      <span className="text-gray-700">{row.key}</span>
      <span className="tabular-nums text-gray-500">
        {Math.round(row.capacity)} of {Math.round(row.demand)} ·{' '}
        <span className={short ? 'text-amber-700' : 'text-emerald-700'}>
          {row.demand > 0
            ? short
              ? `${Math.round(-row.gap)} short`
              : `${Math.round(row.gap)} spare`
            : 'no demand'}
        </span>
      </span>
    </div>
  );
}

/**
 * One cluster's capacity against its demand.
 *
 * ⚠️ THE BAR IS SCALED TO WHICHEVER IS LARGER, with a marker where the demand sits — not filled by
 * `coverage` clamped to 1. Clamping made 112%, 175% and 246% three identical full-width bars: every
 * cluster with any surplus looked the same as every other, which is three rows of chart carrying no
 * information. This way a surplus shows the marker short of the fill and a shortfall shows the fill
 * short of the marker, and both are readable at a glance.
 */
function ClusterRow({ row }: { row: ClusterCoverage }) {
  const short = row.gap < -0.5;
  const scale = Math.max(row.demand, row.capacity, 1);
  return (
    <div data-testid={`coverage-${row.key}`} className="pt-1">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="font-medium text-gray-900">{row.key}</span>
        <span className="text-gray-500 tabular-nums">
          {row.fte.toFixed(1)} FTE
          {row.interns > 0.005 ? ` + ${row.interns.toFixed(1)} intern` : ''} ·{' '}
          {Math.round(row.capacity)} of {Math.round(row.demand)} ·{' '}
          <span className={short ? 'text-amber-700' : 'text-emerald-700'}>
            {row.demand > 0 ? pctRaw(row.coverage) : '—'}
          </span>
        </span>
      </div>
      <ProgressBar
        pct={clamp(row.capacity / scale)}
        markerPct={clamp(row.demand / scale)}
        tone={short ? 'warn' : 'ok'}
      />
    </div>
  );
}
